import { has, isArray, map, trim } from "lodash";
import React, { useCallback } from "react";

import styled from "@emotion/styled";
import transition from "~/utilities/transition";

import BaseLink from "~/components/links/BaseLink";
import FaIcon from "~/components/FaIcon";
import CADIcon from "~/components/CADIcon";
import GatedDocumentButton from "~/components/buttons/GatedDocumentButton";

import { sanitizeAndParseHTML } from "~/utilities/helpers";
import { getUrlFromElasticsearch } from "~/utilities/searchHelpers";

import useDocumentTableBuilder from "~/hooks/useDocumentTableBuilder";
import useGetBasePath from "~/hooks/useGetBasePath";

const SearchTitle = styled(BaseLink)`
    ${transition({}, "color")};
    font-size: 1.8rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.typography.links};

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.colors.typography.linksHover};
    }
`;

export const DocTitle = styled.span`
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.2;
`;

const Icon = styled(FaIcon)`
    margin-right: 10px;
`;

const getTitleLink = (url, iconName, title) => {
    return (
        <SearchTitle to={url}>
            <Icon name={iconName} />
            <DocTitle>{sanitizeAndParseHTML(title)}</DocTitle>
        </SearchTitle>
    );
};

const getIcon = (iconName) => {
    if (iconName === "cad") {
        return <CADIcon withTitle />;
    }

    return <Icon name={iconName} />;
};

const getGatedDocumentButton = (doc, clearParentState = null) => {
    return (
        <GatedDocumentButton
            doc={doc}
            inline
            isCad={doc.iconName === "cad"}
            clearParentState={clearParentState}
        >
            {doc.iconName === "cad" ? (
                <CADIcon withTitle />
            ) : (
                <Icon name={doc.iconName} />
            )}
            <DocTitle>{sanitizeAndParseHTML(doc.title)}</DocTitle>
        </GatedDocumentButton>
    );
};

const getVariantSku = (elasticsearchDocument) => {
    const meta = elasticsearchDocument?.meta;
    const product_variant_sku = meta?.product_variant_sku;
    const skuObj = isArray(product_variant_sku) ? product_variant_sku[0] : {};
    return has(skuObj, "value") ? skuObj.value : "";
};

const getTitleText = (elasticsearchDocument) => {
    const postTitle = elasticsearchDocument?.post_title || "";
    const productVariantSku = getVariantSku(elasticsearchDocument);

    if (productVariantSku) {
        return `${postTitle}, ${productVariantSku}`;
    }

    return sanitizeAndParseHTML(postTitle);
};

const useBuildSearchResults = () => {
    const { buildDocumentForSearch, DOWNLOAD_POST_TYPE } =
        useDocumentTableBuilder();
    const getBasePath = useGetBasePath(true);

    const formatSearchResults = useCallback(
        (elasticsearchDocument, doc = null) => {
            const post_id = elasticsearchDocument?.post_id || "";
            const postTitle = getTitleText(elasticsearchDocument);
            const lang = elasticsearchDocument?.post_lang || "";
            const postType = elasticsearchDocument?.post_type || "";
            const year =
                elasticsearchDocument?.post_date?.substring(0, 4) || "";
            const url = getUrlFromElasticsearch(
                elasticsearchDocument,
                getBasePath,
            );

            let iconName = "link";
            let formattedTitle = getTitleLink(url, iconName, postTitle);

            if (doc) {
                iconName = doc.iconName;
                formattedTitle = getGatedDocumentButton(doc);
            }

            const result = {
                post_id,
                lang,
                year,
                postType,
                postTitle,
                iconName,
                title: formattedTitle,
                type: postType,
            };

            return result;
        },
        [getBasePath],
    );

    const buildSearchTable = useCallback(
        (hits) => {
            return map(hits, (elasticsearchNode) => {
                const elasticsearchDocument = elasticsearchNode._source;
                const postType = elasticsearchDocument?.post_type;

                let doc = null;

                if (trim(postType) === DOWNLOAD_POST_TYPE) {
                    doc = buildDocumentForSearch(elasticsearchDocument);
                }

                return formatSearchResults(elasticsearchDocument, doc);
            });
        },
        [formatSearchResults, buildDocumentForSearch, DOWNLOAD_POST_TYPE],
    );

    const buildAutocompleteTable = useCallback(
        (hits) => {
            const results = map(hits, (elasticsearchNode) => {
                const elasticsearchDocument = elasticsearchNode._source;
                const postType = elasticsearchDocument?.post_type;

                if (trim(postType) === DOWNLOAD_POST_TYPE) {
                    return buildDocumentForSearch(elasticsearchDocument);
                }

                return formatSearchResults(elasticsearchDocument);
            });

            return map(results, (result) => {
                const icon = getIcon(result.iconName);
                const resourceType = `${
                    icon.props.name
                        ? icon.props.name
                              .replace("file-", "")
                              .replace("play", "video")
                              .replace("zipper", "compressed file")
                              .replace("alt", "file")
                        : "compressed file"
                } :`;
                return {
                    title: result.postTitle,
                    doc: {
                        getIcon: () => {
                            return getIcon(result.iconName);
                        },
                        getSearchButton: (clearParentState) => {
                            if (result.postType === DOWNLOAD_POST_TYPE) {
                                return getGatedDocumentButton(
                                    result,
                                    clearParentState,
                                );
                            }
                            const formattedTitle = result.title;
                            return formattedTitle;
                        },
                    },
                    resourceType,
                };
            });
        },
        [formatSearchResults, buildDocumentForSearch, DOWNLOAD_POST_TYPE],
    );

    return { buildSearchTable, buildAutocompleteTable };
};

export default useBuildSearchResults;
